import { findLast, flatten } from 'lodash';
import {
    cancelAndCreatHomeworkResponse,
    createHomeworkResponse,
    fetchHomework,
    fetchHomeworkHistory,
    updateHomeworkResponse,
} from '@/api/homeworkApi.js';
import { StatusesMap, TaskTypes } from '@/admin/components/Homework/constants.js';

export default {
    namespaced: true,
    state: {
        homeworkId: null,
        homework: {},
        history: {},
        historyMetaData: {},
        currentCursor: null,
        editableItem: null,
        loaders: {
            list: false,
            bottom: false,
        },
    },
    getters: {
        homework: state => state.homework,
        homeworkId: state => state.homeworkId,
        history: state => state.history,
        historyList: state => flatten(Object.values(state.history)),
        student: state => state.homework.student,
        status: state => state.homework.status,
        historyMetaData: state => state.historyMetaData,
        editableItem: state => state.editableItem,
        currentCursor: state => state.currentCursor,
        isListLoading: state => state.loaders.list,
        isBottomLoading: state => state.loaders.bottom,
        isShowBottomActions: state => state.history[state.currentCursor] && (state.history[state.currentCursor]?.length < state.historyMetaData?.per_page || !state.historyMetaData?.next_cursor),
        lastVersion: (state, getters) => findLast(getters.historyList, item => item.type !== TaskTypes.RESPONSE),
        lastResponse: (state, getters) => findLast(getters.historyList, item => item.type === TaskTypes.RESPONSE),
        isAccepted: (state, getters) => getters.homework?.status === StatusesMap.ACCEPTED,
        isEmpty: (state, getters) => !getters.historyList.length,
        lastHistoryItemId: (state, getters) => target => getters.historyList.find(item => item.id === target.last_version_id)?.id,
    },
    mutations: {
        setHistory(state, value) {
            state.history = value;
        },
        setCurrentCursor(state, value) {
            state.currentCursor = value;
        },
        updateHistory(state, value) {
            state.history[value.key] = value.value;
        },
        setHomework(state, value) {
            state.homework = value;
        },
        setHomeworkId(state, value) {
            state.homeworkId = value;
        },
        setHistoryMetaData(state, value) {
            state.historyMetaData = value;
        },
        setEditableItem(state, value) {
            state.editableItem = value;
        },
        setLoaders(state, value) {
            state.loaders[value.name] = value.show;
        },
    },
    actions: {
        resetHistory({ commit }) {
            commit('setHistory', {});
            commit('setHistoryMetaData', {});
        },
        resetEditableItem({ commit }) {
            commit('setEditableItem', null);
        },
        updateEditableItem({ commit, getters }, params) {
            commit('setEditableItem', {
                ...getters.editableItem,
                ...params,
            });
        },
        async fetchHistory({ commit, state }, params = {}) {
            const homeworkId = params.homeworkId || state.homework?.id;

            if (!homeworkId) return;

            try {
                const { data } = await fetchHomeworkHistory(homeworkId, state.currentCursor);

                commit('updateHistory', {
                    key: state.currentCursor,
                    value: data.data,
                });
                commit('setHistoryMetaData', data.meta);
            } catch {
                throw new Error('fetchHistory: Ошибка получения истории ДЗ');
            }
        },
        async fetchHistoryNextPage({ getters, dispatch, commit }) {
            if (!getters.historyMetaData.next_cursor) return;

            await commit('setCurrentCursor', getters.historyMetaData.next_cursor);
            await dispatch('fetchHistory');
        },
        async fetchHomework({ commit }, params) {
            const { homeworkId } = params;

            if (!homeworkId) return;

            try {
                const { data } = await fetchHomework(homeworkId);

                commit('setHomework', data.data);
            } catch {
                throw new Error('fetchHomework: Ошибка получения ДЗ');
            }
        },
        async updateHistory({ state, dispatch }) {
            const { homeworkId } = state;

            await dispatch('fetchHomework', { homeworkId });
            await dispatch('fetchHistory', { homeworkId });
        },
        async changeHomeworkStatus({ dispatch, getters }, { status }) {
            const requestBody = await dispatch('generateBaseRequestBody', {
                versionId: getters.editableItem.historyItem.id,
                data: {
                    status,
                },
            });

            return dispatch(
                'requestWithListLoader',
                () => createHomeworkResponse(requestBody),
            );
        },
        async cancelAndCreateHomeworkResponse({ dispatch, getters }, { status, responseItem }) {
            const requestBody = await dispatch('generateBaseRequestBody', {
                versionId: getters.lastVersion.id,
                status,
                responseId: responseItem?.id || getters.editableItem?.historyItem.id,
                data: {
                    new_status: status,
                },
            });

            return dispatch(
                'requestWithListLoader',
                () => cancelAndCreatHomeworkResponse(requestBody),
            );
        },
        async updateHomeworkResponse({ dispatch, getters }) {
            const requestBody = await dispatch('generateBaseRequestBody', {
                versionId: getters.lastVersion.id,
                responseId: getters.editableItem.historyItem.id,
            });

            return dispatch(
                'requestWithListLoader',
                () => updateHomeworkResponse(requestBody),
            );
        },
        generateBaseRequestBody({ state }, { versionId, responseId, data, homeworkId }) {
            return {
                homeworkId: homeworkId || state.homework.id,
                versionId,
                responseId,
                data: {
                    comment: state.editableItem?.text || '',
                    ...data,
                },
            };
        },
        async requestWithListLoader({ dispatch }, request) {
            await dispatch('showListLoader');

            const { data } = await request();

            await dispatch('updateHistory');
            await dispatch('resetEditableItem');
            await dispatch('hideListLoader');

            return data;
        },
        async getLastTaskId({ dispatch, getters }, historyItem) {
            if (getters.lastHistoryItemId(historyItem)) {
                await dispatch('hideListLoader');

                return getters.lastHistoryItemId(historyItem);
            }

            if (getters.historyMetaData.next_cursor) {
                await dispatch('showListLoader');
                await dispatch('fetchHistoryNextPage');

                return dispatch('getLastTaskId', historyItem);
            }
        },
        updateActiveHomework({ commit }, id) {
            commit('setHomeworkId', id);
        },
        showListLoader({ commit }) {
            commit('setLoaders', {
                name: 'list',
                show: true,
            });
        },
        showBottomLoader({ commit }) {
            commit('setLoaders', {
                name: 'bottom',
                show: true,
            });
        },
        hideListLoader({ commit }) {
            commit('setLoaders', {
                name: 'list',
                show: false,
            });
        },
        hideBottomLoader({ commit }) {
            commit('setLoaders', {
                name: 'bottom',
                show: false,
            });
        },
    },
};
